<template>
  <div class="flex grow">
    <Component
      :is="!isEmpty(tooltip) && !maxTablet ? Tooltip : 'div'"
      :title="tooltip"
      class="flex grow"
      side="bottom"
    >
      <div
        class="flex flex-col shrink-0 grow p-3 gap-2 bg-secondary border border-secondary rounded-md"
        :class="{
          'w-40': viewport !== 'desktop',
          'h-19': viewport === 'desktop',
          'lg:w-auto lg:h-19': viewport === 'reactive',
        }"
      >
        <div class="flex items-center gap-1">
          <Icon :name="iconName" variant="fill" size="xl" :class="iconClass" />
          <span
            class="text-secondary font-medium"
            :class="{
              'text-style-base': viewport !== 'desktop',
              'text-style-sm': viewport === 'desktop',
              'lg:text-style-sm': viewport === 'reactive',
            }"
          >
            {{ label }}
          </span>
        </div>
        <div
          class="max-w-max flex gap-0.5"
          :class="{
            'flex-col': viewport === 'mobile',
            'items-center': viewport === 'desktop',
            'max-lg:flex-col lg:items-center': viewport === 'reactive',
          }"
        >
          <span
            class="text-primary font-semibold"
            :class="{
              'text-style-3xl': viewport !== 'desktop',
              'text-style-xl': viewport === 'desktop',
              'lg:text-style-xl': viewport === 'reactive',
            }"
          >
            {{ count }}
          </span>
          <template v-if="secondaryCount && badgeLabel">
            <div
              v-if="!isMobile"
              class="flex items-center py-1.5 px-2 self-stretch"
            >
              <Separator
                class="w-px self-stretch bg-[#D9D9D9]"
                orientation="vertical"
                decorative
              />
            </div>
            <Badge
              :label="badgeLabel"
              type="light"
              :size="isMobile ? 'lg' : 'sm'"
              color="error"
            />
          </template>
        </div>
      </div>
    </Component>
  </div>
</template>

<script setup>
import { computed } from "vue";
import { isEmpty } from "lodash";
import { Separator } from "radix-vue";
import { Badge } from "@/SharedComponents/Badges";
import { Icon } from "@/SharedComponents/Icon";
import { useBreakpoints } from "@/Composables/useBreakpoints";
import { useI18n } from "vue-i18n";
import { Tooltip } from "@/SharedComponents/Tooltips";

const props = defineProps({
  type: { type: String, required: true },
  tooltip: { type: String, required: false },
  count: { type: [Number, String], required: false },
  secondaryCount: { type: [Number, String], required: false },
  viewport: {
    type: String,
    validator: (val) => ["mobile", "desktop", "reactive"].includes(val),
    default: "reactive",
  },
});

const iconName = computed(() => {
  switch (props.type) {
    case "tasks":
      return "board-tasks";
    case "files":
      return "documents";
    case "assets":
      return "asset";
    default:
      return "placeholder";
  }
});

const iconClass = computed(() => {
  switch (props.type) {
    case "tasks":
      return "fg-brand-primary";
    case "files":
      return "text-utility-success-500";
    case "assets":
      return "text-utility-orange-500";
    default:
      return "";
  }
});

const { t } = useI18n();

const label = computed(() => {
  switch (props.type) {
    case "tasks":
      return t("admin.tasks.label", 2);
    case "files":
      return t("admin.files.label", 2);
    case "assets":
      return t("admin.assets.label", 2);
    default:
      return "";
  }
});

const badgeLabel = computed(() => {
  switch (props.type) {
    case "tasks":
      return t("admin.counts.tasks.overdue", {
        count: props.secondaryCount,
      });
    case "files":
      return t("admin.counts.files.expired", {
        count: props.secondaryCount,
      });
    default:
      return "";
  }
});

const { maxTablet } = useBreakpoints();

const isMobile = computed(() => {
  switch (props.viewport) {
    case "mobile":
      return true;
    case "desktop":
      return false;
    case "reactive":
    default:
      return maxTablet.value;
  }
});
</script>
